/**
 * Client-side shapes and defaults for the document-extraction eval runner.
 *
 * These mirror the DE `/internal/eval` zod contracts (`evalSelectionConfigSchema`,
 * `evalRunConfigSchema` in the document-extraction service). The backend proxy
 * passes both config blobs through as opaque `JSON`, so this file is the only
 * place the dashboard encodes the DE field names — kept deliberately narrow and
 * annotated so it is easy to reconcile against the DE schema when it evolves.
 */

/** Providers the run launcher can target (DE `evalRunConfigSchema.provider`). */
export const EVAL_PROVIDERS = ["openai", "anthropic"] as const;
export type EvalProvider = (typeof EVAL_PROVIDERS)[number];

/**
 * Known model ids prefilled per provider (the locked extraction chain). Free-form
 * entry is still allowed — an unknown model just forces a pricing override.
 */
export const KNOWN_MODELS_BY_PROVIDER: Record<EvalProvider, readonly string[]> =
  {
    openai: ["gpt-5.4", "gpt-5.5"],
    anthropic: ["claude-sonnet-5"],
  };

/**
 * Reasoning-effort presets. The value is free-form on the DE side; an empty
 * selection omits the key and takes the provider default.
 */
export const REASONING_EFFORT_OPTIONS = [
  "",
  "minimal",
  "low",
  "medium",
  "high",
] as const;

/** The stratification axes a set may use (DE `evalSelectionAxisSchema`). */
export const SELECTION_AXES = [
  "company",
  "document_type",
  "approval_tier",
] as const;
export type SelectionAxis = (typeof SELECTION_AXES)[number];

/** GT trust tiers eligible for selection (DE `evalApprovalTierSchema`). */
export const APPROVAL_TIERS = ["human_approved", "historical_seed"] as const;
export type ApprovalTier = (typeof APPROVAL_TIERS)[number];

/** Execution modes (the run's `execution_mode` column, not part of `config`). */
export type ExecutionMode = "sync" | "batch";

/**
 * The canonical retrieval knobs for the measured chain (DE
 * `CANON_RETRIEVAL_STRATEGY`: k=3, λ=0.5, overfetch=100). Prefilled in the
 * launcher; every knob stays operator-editable.
 */
export const CANON_RETRIEVAL = {
  k: 3,
  mmrLambda: 0.5,
  overfetch: 100,
} as const;

export const DEFAULT_MAX_OUTPUT_TOKENS = 4096;
export const DEFAULT_CONCURRENCY = 4;
export const DEFAULT_TARGET_SIZE = 100;

/** The selection config posted to `previewDocExtractionEvalSet` / `createDocExtractionEvalSet`. */
export type EvalSelectionConfig = {
  method: "proportional_stratified";
  targetSize: number;
  seed: string;
  axes: SelectionAxis[];
  noveltyAxis: boolean;
  approvalTiers: ApprovalTier[];
  timeDisjoint: boolean;
  minPerCompany?: number;
  capPerCompany?: number;
  requireOcrCache: boolean;
  companyAllowlist?: string[];
};

export type EvalRetrievalConfig = {
  k: number;
  mmrLambda: number;
  overfetch: number;
};

export type EvalPricingOverride = {
  inputPerMTok: number;
  outputPerMTok: number;
  batchInputPerMTok?: number;
  batchOutputPerMTok?: number;
};

/** The run config posted to `estimateDocExtractionEvalRun` / `createDocExtractionEvalRun`. */
export type EvalRunConfig = {
  provider: EvalProvider;
  modelId: string;
  reasoningEffort?: string;
  retrieval: EvalRetrievalConfig;
  includePageImages: boolean;
  maxOutputTokens: number;
  concurrency: number;
  pricingOverride?: EvalPricingOverride;
};

/** Per-leaf-stratum census returned by preview and frozen onto a set. */
export type StratumSummary = {
  stratum: {
    company?: string;
    documentType?: string;
    approvalTier?: string;
    novelty?: string;
  };
  candidateCount: number;
  selectedCount: number;
};

/** Run lifecycle statuses (DE `eval_run.status`). */
export const TERMINAL_RUN_STATUSES: ReadonlySet<string> = new Set([
  "COMPLETED",
  "FAILED",
  "CANCELLED",
]);

export const isTerminalStatus = (status: string): boolean =>
  TERMINAL_RUN_STATUSES.has(status);

/** MUI Chip color for a run status. */
export const runStatusColor = (
  status: string,
): "default" | "info" | "success" | "error" | "warning" => {
  switch (status) {
    case "COMPLETED": {
      return "success";
    }
    case "FAILED": {
      return "error";
    }
    case "CANCELLED": {
      return "warning";
    }
    case "PENDING": {
      return "default";
    }
    default: {
      // RUNNING, SUBMITTING_BATCH, AWAITING_BATCH, INGESTING_BATCH.
      return "info";
    }
  }
};

/** MUI Chip color for a per-document status. */
export const docStatusColor = (
  status: string,
): "default" | "success" | "error" | "warning" => {
  switch (status) {
    case "COMPLETED": {
      return "success";
    }
    case "ERROR": {
      return "error";
    }
    case "SKIPPED": {
      return "warning";
    }
    default: {
      return "default";
    }
  }
};
